import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Users, Zap, CheckCircle } from 'lucide-react';
import { ArchitectIcon, PerfectionistIcon, WatcherIcon } from './CustomIcons';
import { Button } from './ui/button';
import { InteractiveCard } from './InteractiveCard';
import Footer from './Footer';

export default function HomePage() {
  useEffect(() => {
    document.title = 'Semando - Elite Tech Specialists for Critical Systems | AI, QA Testing, Cyber Security';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Deploy elite tech specialists when failure isn\'t an option. The Architect (AI Implementation), The Perfectionist (QA Testing), and The Watcher (Cyber Security) for mission-critical systems.');
    }
  }, []);
  const services = [
    {
      icon: ArchitectIcon,
      title: 'AI Implementation',
      subtitle: 'The Architect',
      description: 'Intelligent systems that learn, adapt, and evolve with your business needs.',
      href: '/ai-implementation',
      price: '$600'
    },
    {
      icon: PerfectionistIcon,
      title: 'QA & Testing',
      subtitle: 'The Perfectionist',
      description: 'Comprehensive testing that ensures flawless performance under any condition.',
      href: '/qa-testing',
      price: '$450'
    },
    {
      icon: WatcherIcon,
      title: 'Cyber Security',
      subtitle: 'The Watcher',
      description: 'Advanced threat detection and response systems that never sleep.',
      href: '/cyber-security',
      price: '$550'
    }
  ];

  const scenarios = [
    {
      title: 'System Breach Detected',
      description: 'Your monitoring alerts show unauthorized access attempts at 3 AM.',
      solution: 'Immediate threat containment and forensic analysis',
      service: 'Cyber Security'
    },
    {
      title: 'AI Model Underperforming',
      description: 'Your machine learning predictions are becoming less accurate over time.',
      solution: 'Model optimization and retraining with fresh data',
      service: 'AI Implementation'
    },
    {
      title: 'Critical Bug in Production',
      description: 'A major software update introduced bugs affecting thousands of users.',
      solution: 'Rapid automated testing and rollback procedures',
      service: 'QA & Testing'
    }
  ];

  return (
    <div className="min-h-screen space-y-0">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden" aria-labelledby="hero-heading">
        <div className="absolute inset-0 cyber-grid-wrapper absolute">
          <div className="cyber-grid absolute opacity-20" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                <img src="/logo.svg" alt="Semando" className="h-5 w-5 mr-2" />
                <span className="text-sm text-primary">SYSTEMS_ONLINE</span>
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl tracking-tight" id="hero-heading">
                <span className="block">Elite Tech Specialists</span>
                <span className="block text-primary">For Critical Systems</span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                When failure isn't an option, you need specialists who operate in the shadows of technology—
                <span className="text-primary">The Architect</span>, <span className="text-primary">The Perfectionist</span>, and <span className="text-primary">The Watcher</span>.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="group bg-primary text-primary-foreground hover:bg-primary/90 cyber-glow">
                <Link to="/contact">
                  Deploy Specialists
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="group border-primary/20 hover:border-primary/40"
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  servicesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                View Capabilities
                <Code className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section id="services" className="py-24 bg-gradient-to-r from-muted/5 via-transparent to-muted/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-16">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl">Our Expertise</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Three specialists. Infinite possibilities. Each master of their domain, ready to solve your most complex challenges.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <InteractiveCard key={index} className="hover-neon">
                  <div className="p-8 space-y-6 text-center">
                    <div className="flex justify-center">
                      <div className="w-20 h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                        <service.icon className="h-12 w-12 text-primary" size={48} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm text-primary uppercase tracking-wider">{service.subtitle}</div>
                      <h3 className="text-2xl">{service.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{service.description}</p>
                    </div>
                    <div className="py-2">
                      <div className="text-6xl text-primary">{service.price}<span className="text-2xl text-muted-foreground">/day</span></div>
                    </div>
                    <Button asChild className="w-full group bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground">
                      <Link to={service.href}>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* When You Need Us */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl">When You Need Us</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Critical situations demand immediate expertise. These are the moments when our specialists deploy.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {scenarios.map((scenario, index) => (
                <div
                  key={index}
                  className="p-8 rounded-lg border border-border/50 bg-card hover:border-primary/20 transition-all duration-300 relative"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary/60 rounded-l-lg" />
                  <div className="space-y-4 ml-4">
                    <div className="space-y-2">
                      <div className="text-sm text-primary uppercase tracking-wider">{scenario.service}</div>
                      <h3 className="text-xl">{scenario.title}</h3>
                      <p className="text-muted-foreground">{scenario.description}</p>
                    </div>
                    <div className="pt-4 border-t border-border/30">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span className="text-sm">{scenario.solution}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent relative">
        <div className="absolute inset-0 cyber-grid-wrapper absolute">
          <div className="cyber-grid opacity-20" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-end">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl lg:text-5xl">Why Choose Semando</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  We're not just another tech consultancy. We're specialists who thrive in high-stakes environments where precision and expertise are non-negotiable.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg">Rapid Deployment</h3>
                    <p className="text-muted-foreground">Our specialists can be operational within hours, not weeks.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg">Elite Expertise</h3>
                    <p className="text-muted-foreground">Each specialist brings deep, focused knowledge in their domain.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="h-4 w-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg">Proven Results</h3>
                    <p className="text-muted-foreground">Track record of success in mission-critical environments.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:text-left">
              <div className="inline-block p-8 rounded-lg border border-primary/20 cyber-glow cyber-box">
                <div className="space-y-4">
                  <h3 className="text-2xl">Ready to Deploy?</h3>
                  <p className="text-muted-foreground">
                    Critical situations require immediate action. Our specialists are standing by.
                  </p>
                  <Button size="lg" asChild className="w-full group bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/contact">
                      Contact Us Now
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}